import { faBuilding, faMoon, faNoteSticky, faUser } from '@fortawesome/free-regular-svg-icons'
import { Button, InputAdornment, Stack, Checkbox, FormControlLabel } from '@vapor/material'
import { Dialog, DialogActions, DialogContent, DialogContentText, Grid } from '@mui/material'
import { TextField } from '@mui/material'
import { FormItem, FormNumericField, HICLoading, useConfirm } from 'hic-components'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import { CreditType, CustomerInput, CustomerType, OriginName, SimpleCustomerLookupFragment, useCustomerNewMutation } from 'src/api/api.generated'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { z } from 'zod'
import useReservationInsertFormHook, { formSchemaAsReservationFormData } from './reservation-new.from.hook'
import CompanyLookup from 'src/app/components/customer-lookup/company-lookup'
import { differenceInCalendarDays } from 'date-fns'
import { DatePicker } from '@mui/x-date-pickers-pro'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useCurrentLocale from 'src/app/hooks/current-locale/use-current-locale'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { ReservationCartRow, ReservationNewAdditionalService, ReservationNewFormData, ReservationNewFormProps } from './reservation-new.type'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { addDays, getDayDiff, isValidDate } from 'src/utils/date'
import { dateLowerThan, dateEquality, dateGreaterThan } from 'src/utils/utils'
import { HiCIcon } from 'src/app/components/icons/icon'
import { useReservationNewCRUD } from './reservation-new-crud'
import useReservationNew from './reservation-new.hooks'
import PersonLookup from 'src/app/components/customer-lookup/person-lookup'
import { CustomerDetailsEdit } from 'src/app/components/customer-details/customer-details-edit'
import { readQuoteInSessionStorage, removeQuoteInSessionStorage } from 'src/app/pages/quote/quote-utils'
import ReservationAccordion from '../components/accordion/reservation-accordion'
import AdditionalServicesAccordion from '../components/additional-services/additional-services-accordion'
import ReservationCart from '../components/reservation-cart/reservation-cart'
import ReservationInsertToolbar from '../components/reservation-insert-toolbar'
import { reservationNewCartAsFormData } from './reservation-new.utils'
import { calculateAdditionalServicePrice } from 'src/utils/additional-services-utils'
import { HICTitle } from 'src/app/components/typography/title'
import { isValid } from 'date-fns'
import HicDateRangePicker from 'src/app/components/hic-date-range-picker/hic-date-range-picker'
import { faBallotCheck } from '@fortawesome/pro-regular-svg-icons'
import CreditSingleSelect from 'src/app/components/form-fields/credit-single-select'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { capitalize } from 'lodash'
import RatesMultipleSelect from '../../prices-calendar/components/filters/rates-multiple-select'
import { ChargeCategoriesMultipleSelect } from 'src/app/components/form-fields/charge-categories-multiple-select'
import { faArrowRotateLeft, faBed, faPlus, faPuzzlePiece, faUsers } from '@fortawesome/pro-regular-svg-icons'
import PageHeaderItem from '../components/go-to-group-insertion'
import FindCompanyConventions from 'src/app/components/form-fields/find-company-conventions/find-company-conventions'
import HistoricalCustomerResearch from 'src/app/components/form-fields/historical-customer-research/historical-customer-research'

const DEFAULT_ADULTS_NUMBER = 2
const DEFAULT_DATE = new Date()

export default function ReservationNewForm(props: ReservationNewFormProps) {
  const { formLayoutProps } = props
  const confirm = useConfirm()
  const { id: tenantId } = useTenant()
  const [loading, setLoading] = useState(false)
  const localizeString = useLocalizeString()
  const [cartRows, setCartRows] = useState<ReservationCartRow[]>([])
  const [showChildrenFilter, setShowChildrenFilter] = useState(false)
  const { sm } = useBreakpoints()
  const hotelDate = useHotelDate()

  const {
    loading: CRUDLoading,
    currency,
    reservationRatesQuery,
    reservationRates,
    childrenResult,
    reservationStatesResult,
    resourceTypesResult,
    originsResult,
    reservationsResult,
    additionalServiceResult,
  } = useReservationNewCRUD(props.propertyId, props.reservationId)
  const cartContainerRef = useRef<HTMLDivElement | null>(null)
  const additionalServicesContainerRef = useRef<HTMLDivElement | null>(null)
  const showErrorSnackBar = useErrorSnackBar()
  const successSnackBar = useSuccessSnackBar()

  //#region choices filter
  const [affiliatedRateId, setAffiliatedRateId] = useState<string>()
  const [ratesFilter, setRatesFilter] = useState<string[]>([])
  const [chargeCategoriesFilter, setChargeCategoriesFilter] = useState<string[]>([])
  const [disableFilters, setDisableFilters] = useState<boolean>(props.reservationId !== undefined)
  //#endregion

  //#region overbooking modal
  const [openOverbookingModal, setOpenOverbookingModal] = useState<string[]>([])
  const handleOpenOverbookingModal = (overbookedRowIds: string[]) => setOpenOverbookingModal(overbookedRowIds)
  const handleCloseOverbookingModal = () => setOpenOverbookingModal([])
  //#endregion

  const { schema, errors, control, formValue, watch, setValue, getValues, onFormSubmit, onFormSubmitAndOpenReservation } = useReservationInsertFormHook({
    onSubmit: async (data) => {
      let confirmedStateId = ''
      let optionExpiration: Date | undefined | null = undefined
      if (data.isOptioned) {
        confirmedStateId = reservationStatesResult?.reservationStates?.find((state) => state.optioned === true)?.id ?? ''
        optionExpiration = data.optionedExpirationDate
      } else {
        confirmedStateId = reservationStatesResult?.reservationStates?.find((state) => state.confirmed === true)?.id ?? ''
      }
      const originId = originsResult?.origins?.find((o) => o.name === OriginName.PMS)?.id ?? originsResult?.origins?.[0]?.id
      if (confirmedStateId && originId) {
        if (cartRows.length <= 0) {
          confirm({
            title: t('pms.booking.reservation_new.dialog_empty_cart.title'),
            message: t('pms.booking.reservation_new.dialog_empty_cart.message'),
            confirmButtonText: t('pms.booking.reservation_new.dialog_empty_cart.negative_button'),
            cancelButtonText: t('pms.booking.reservation_new.dialog_empty_cart.positive_button'),
            onConfirm: () => {
              props.onCancel()
            },
          })
        } else {
          const formData: ReservationNewFormData = reservationNewCartAsFormData(tenantId, props.propertyId, originId, data, confirmedStateId, cartRows, optionExpiration)
          const result = await onSubmit(formData, data.customerEmailAddress, currentGuest?.name, currentCompany?.name)
          return result
        }
      }
    },
    openReservation: props.onSubmitAndOpenReservation,
    filter: props.filter,
  })

  const {
    onSubmit,
    submitWithOverbooking,
    loading: loadingSubmit,
  } = useReservationNew({
    tenantId: tenantId,
    reservationId: props.reservationId,
    planningInfo: props.planningInfo,
    next: props.onSubmitComplete,
    handleOpenOverbookingModal,
    failedReservationAddition: (ids: string[]) => {
      setCartRows(cartRows.filter((row) => ids.includes(row.id)))
    },
    overbookedReservationAddition: (ids: string[]) => {
      const overbooked = cartRows.filter((row) => ids.includes(row.id))
      setCartRows(overbooked)
      handleOpenOverbookingModal(overbooked.map((rr) => rr.id))
    },
  })

  type Reservation = z.TypeOf<typeof schema>

  useEffect(() => {
    if (reservationsResult && reservationsResult.reservations && (reservationsResult?.reservations ?? []).length > 0) {
      const startDates = (reservationsResult.reservations[0]?.reservedResources ?? []).map((rr) => rr.start.getTime())
      const endDates = (reservationsResult.reservations[0]?.reservedResources ?? []).map((rr) => rr.end.getTime())

      if (startDates) {
        const minDate = new Date(Math.min(...startDates))
        setValue('reservation.checkIn', minDate)
      }
      if (endDates) {
        const maxDate = new Date(Math.max(...endDates))
        setValue('reservation.checkOut', maxDate)
      }

      if (reservationsResult.reservations[0].notes && reservationsResult.reservations[0].notes.length > 0) {
        setValue('notes', reservationsResult.reservations[0].notes[0].description)
      }

      setValue('customerPersonId', reservationsResult.reservations[0].reference.customerPersonId ?? reservationsResult.reservations[0].reference.mainGuestId)
      setValue('customerCompanyId', reservationsResult.reservations[0].reference.customerCompanyId ?? null)
      setValue('disableAutomation', reservationsResult.reservations[0].disableAutomation ?? false)
    }
  }, [reservationsResult])

  const resetOccupancy = () => {
    setValue('adults', DEFAULT_ADULTS_NUMBER)
    formValue.children?.forEach((child, index, array) => (array[index].quantity = 0))
    setValue('children', [...(formValue.children ?? [])])
  }

  const childExist = useMemo(() => {
    if (formValue.children && formValue.children.length > 0) {
      return formValue.children?.map((c) => c.quantity)?.reduce((acc, q) => acc + q) > 0
    }
    return false
  }, [JSON.stringify(formValue.children)])

  const confirmCartItemReset = (onConfirm: () => void) => {
    const cartHasItems = cartRows.length > 0
    if (cartHasItems) {
      confirm({
        title: t('pms.booking.reservation_new.reset_cart.title'),
        message: t('pms.booking.reservation_new.reset_cart.message'),
        confirmButtonText: t('pms.booking.reservation_new.clean_cart'),
        cancelButtonText: t('commons.no'),
        onConfirm: () => {
          setCartRows([])
          onConfirm()
        },
      })
    } else {
      onConfirm()
    }
  }

  const onReservationDateRangeChange = (dateRange: { checkIn: Date | null | undefined; checkOut: Date | null | undefined }) => {
    if (dateRange.checkIn && dateRange.checkOut) {
      const nights = differenceInCalendarDays(dateRange.checkOut, dateRange.checkIn)
      if (formValue.nights !== nights) {
        setValue('nights', nights)
      }
    }
  }

  const onNightsChange = (nights: number) => {
    const checkIn = formValue.reservation.checkIn
    if (checkIn) {
      const checkOut = new Date(checkIn)
      checkOut.setDate(checkOut.getDate() + nights)
      const reservation = { checkIn, checkOut }
      if (JSON.stringify(formValue.reservation) !== JSON.stringify(reservation)) {
        setValue('reservation', reservation)
      }
    }
  }

  useEffect(() => {
    setLoading(CRUDLoading || loadingSubmit)
  }, [CRUDLoading, loadingSubmit])

  const accordionDetails: JSX.Element = useMemo(() => {
    return (
      <ReservationAccordion
        navigateToAdditionalServices={() => {
          additionalServicesContainerRef.current?.scrollIntoView({ behavior: 'smooth' })
        }}
        planningResourceTypeId={props.planningInfo?.resourceTypeId}
        resources={reservationRates?.rateQuery.resources?.filter((r) => !r.resourceType.timeConfiguration) ?? []}
        rates={
          reservationRates?.rateQuery.rates
            ?.filter((rate) => {
              if (ratesFilter.length > 0) {
                return ratesFilter.includes(rate.rate.id)
              }
              return true
            })
            .map((rate) => {
              if (chargeCategoriesFilter.length > 0) {
                const filteredCC = rate.resourceRates?.filter((rr) => {
                  return chargeCategoriesFilter.includes(rr.chargeCategoryId)
                })

                return {
                  ...rate,
                  resourceRates: filteredCC,
                  rate: {
                    ...rate.rate,
                    isAffiliatedCompany: affiliatedRateId !== undefined && rate.rate.id === affiliatedRateId,
                  },
                }
              }
              return {
                ...rate,
                rate: {
                  ...rate.rate,
                  isAffiliatedCompany: affiliatedRateId !== undefined && rate.rate.id === affiliatedRateId,
                },
              }
            }) ?? []
        }
        nights={formValue.nights}
        adults={formValue.adults}
        children={formValue.children ?? []}
        checkIn={formValue.reservation?.checkIn ?? DEFAULT_DATE}
        checkOut={formValue.reservation?.checkOut ?? DEFAULT_DATE}
        currency={currency}
        cartRows={cartRows}
        addCartRow={(reservation, showSuccessMessage) => {
          if (showSuccessMessage) successSnackBar(t('pms.booking.reservation_new.add_success_message'))
          if (!cartRows.includes(reservation)) {
            cartRows.push(reservation)
            setCartRows([...cartRows])
          }
        }}
        isPage={props.formLayoutProps.appearance.type === 'PAGE'}
      />
    )
  }, [
    ratesFilter,
    chargeCategoriesFilter,
    reservationRates?.rateQuery?.resources,
    reservationRates?.rateQuery.rates,
    formValue.nights,
    formValue.adults,
    formValue.children,
    formValue.reservation?.checkIn,
    formValue.reservation?.checkOut,
    cartRows,
    affiliatedRateId,
  ])

  const cartComponent = useMemo(() => {
    return (
      <ReservationCart
        propertyId={props.propertyId}
        isEditable={true}
        loading={loading}
        rows={cartRows}
        onDeleteRow={(row) => {
          const rowToDelete = cartRows.find((r) => r.id === row.id)
          if (rowToDelete) {
            const updatedRows = cartRows.filter((r) => r.id !== row.id)
            if (rowToDelete.isFirstInsertion && updatedRows.length > 0) {
              updatedRows[0].isFirstInsertion = true
            }
            setCartRows(updatedRows)
          }
        }}
        onUpdateRow={(row) => {
          if (Array.isArray(row)) {
            row.forEach((r) => {
              const index = cartRows.findIndex((row) => r.id === row.id)
              if (index >= 0) {
                cartRows[index] = { ...r }
              }
            })
            setCartRows([...cartRows])
          } else {
            const index = cartRows.findIndex((r) => r.id === row.id)
            if (index >= 0) {
              cartRows[index] = { ...row }
              cartRows[index].addedAdditionalServices = [...(row.addedAdditionalServices ?? [])]
              setCartRows([...cartRows])
            }
          }
        }}
        onResetRowsPrices={() => {
          setCartRows(
            cartRows.map((row) => {
              return { ...row, averagePrice: row.initialPrice }
            }),
          )
        }}
        title={t('pms.booking.reservation_new.cart')}
      />
    )
  }, [JSON.stringify(cartRows)])

  const adultsOccupancy = useMemo(() => {
    return cartRows.map((row) => row.occupancy.adults).reduce((acc, val) => acc + val, 0)
  }, [cartRows])

  const childrenOccupancy = useMemo(() => {
    return cartRows.map((row) => row.occupancy.children?.map((child) => child.quantity).reduce((acc, val) => acc + val, 0)).reduce((acc, val) => acc + val, 0)
  }, [cartRows])

  const { hicLanguage } = useCurrentLocale()

  const enabledAdditionalServices = useMemo(() => {
    const tmpAdditionalServices: ReservationNewAdditionalService[] = []

    const isValid = (start?: Date | null, end?: Date | null) => {
      if (formValue.reservation?.checkIn && formValue.reservation?.checkOut) {
        if (start) {
          if (dateLowerThan(formValue.reservation?.checkIn, start) && !dateEquality(formValue.reservation?.checkIn, start)) {
            return false
          }
        }
        if (end) {
          if (dateGreaterThan(formValue.reservation?.checkOut, end) && !dateEquality(formValue.reservation?.checkOut, end)) {
            return false
          }
        }
        return true
      }
      return false
    }

    additionalServiceResult?.additionalServices
      ?.filter((as) => as && as.auditable.state === 'ACTIVE' && isValid(as.validity?.start, as.validity?.end))
      .filter((as) => as && (as.minStay ?? 0) <= formValue.nights)
      .forEach((additionalService) => {
        if (additionalService) {
          tmpAdditionalServices.push({
            enabled: false,
            additionalServiceId: additionalService.id,
            priceZero: false,
            additionalService: additionalService,
          })
        }
      })
    return tmpAdditionalServices
  }, [reservationRates?.rateQuery.rates, cartRows, additionalServiceResult?.additionalServices])

  // #region Associate the new reserved resources to an existing reservation
  const tmpTomorrow = new Date()
  tmpTomorrow.setDate(tmpTomorrow.getDate() + 1)
  const [reservationDateRange, setReservationDateRange] = useState<[Date, Date]>([new Date(), tmpTomorrow])
  const [reservationNights, setReservationNights] = useState<number>(1)
  const [customerCompanyId, setCustomerCompanyId] = useState<string | undefined>()
  const [customerPersonId, setCustomerPersonId] = useState<string | undefined>()
  const [openOptionedDatePicker, setOpenOptionedDatePicker] = useState<boolean>(false)
  useEffect(() => {
    if (watch('reservation.checkIn') && watch('reservation.checkOut')) {
      setReservationDateRange([watch('reservation.checkIn') ?? new Date(), watch('reservation.checkOut') ?? tmpTomorrow])
      setReservationNights(differenceInCalendarDays(watch('reservation.checkOut') ?? tmpTomorrow, watch('reservation.checkIn') ?? new Date()))
    }
  }, [watch('reservation.checkIn'), watch('reservation.checkOut')])

  useEffect(() => {
    setCustomerCompanyId(watch('customerCompanyId') ?? undefined)
  }, [watch('customerCompanyId')])

  useEffect(() => {
    setCustomerPersonId(watch('customerPersonId'))
  }, [watch('customerPersonId')])
  //#endregion

  const [currentGuest, setCurrentGuest] = useState<SimpleCustomerLookupFragment | null>(null)
  const [currentCompany, setCurrentCompany] = useState<SimpleCustomerLookupFragment | null>(null)
  const [createCustomerMutation] = useCustomerNewMutation()
  const createNewCustomer = useCallback(
    async (name: { lastName: string; firstName: string }, customerType: CustomerType) => {
      const input: CustomerInput = {
        tenantId: tenantId,
        language: hicLanguage,
        lastName: name.lastName,
        firstName: name.firstName,
        name: `${name.lastName} ${name.firstName}`,
        type: customerType,
      }
      try {
        setLoading(true)
        const result = await createCustomerMutation({ variables: { input: input } })
        const customer = result.data?.createCustomer
        if (!customer) {
          return { type: 'FAIL', errors: result.errors }
        }
        return { type: 'SUCCESS', customer }
      } catch (e) {
        return { type: 'FAIL' }
      } finally {
        setLoading(false)
      }
    },
    [tenantId, createCustomerMutation],
  )
  const onBlurHandlerPersonLookup = async (name: { lastName: string; firstName: string }) => {
    const createdCustomer = await createNewCustomer(name, 'PERSON')
    if (createdCustomer.type === 'ERROR') {
      showErrorSnackBar(createdCustomer.errors)
      setValue('customerPersonId', '')
    } else {
      setCurrentGuest(createdCustomer.customer || null)
      setValue('customerPersonId', createdCustomer.customer?.id || '')
    }
  }
  const onBlurHandlerCompanyLookup = async (name: { lastName: string; firstName: string }) => {
    const createdCustomer = await createNewCustomer(name, 'COMPANY')
    if (createdCustomer.type === 'ERROR') {
      showErrorSnackBar(createdCustomer.errors)
    } else {
      setCurrentCompany(createdCustomer.customer || null)
      if (createdCustomer.customer?.id) {
        setValue('customerCompanyId', createdCustomer.customer?.id) // FIXME: risolvere il problema di disassociazione dell'anagrafica.
        setValue('credit', createdCustomer.customer.company?.defaultCredit ?? CreditType.NC)
      }
    }
  }

  useEffect(() => {
    if (isValidDate(formValue.reservation?.checkIn) && isValidDate(formValue.reservation?.checkOut)) {
      reservationRatesQuery({
        variables: {
          filter: {
            tenantId: tenantId,
            propertyId: props.propertyId,
            start: formValue.reservation?.checkIn ?? (props.filter?.start ? props.filter.start : DEFAULT_DATE),
            end: formValue.reservation?.checkOut ?? (props.filter?.end ? props.filter.end : DEFAULT_DATE),
            customerId: customerCompanyId ? customerCompanyId : undefined,
            resources: [
              {
                adults: formValue.adults ?? DEFAULT_ADULTS_NUMBER,
                childOccupancy: formValue.children,
                quantity: 1,
              },
            ],
          },
        },
      })
    }
  }, [formValue.adults, JSON.stringify(formValue.children), formValue.reservation?.checkIn, formValue.reservation?.checkOut, props.filter?.start, props.filter?.end, customerCompanyId])

  useEffect(() => {
    const quote = readQuoteInSessionStorage()
    removeQuoteInSessionStorage()

    if (quote) {
      let checkIn = new Date()
      if (quote.checkIn) {
        checkIn = new Date(quote.checkIn)
      }
      const theDayAfter = new Date(checkIn)
      theDayAfter.setDate(theDayAfter.getDate() + 1)
      let checkOut = theDayAfter
      if (quote.checkOut) {
        checkOut = new Date(quote.checkOut)
      }

      setValue('reservation.checkIn', checkIn)
      setValue('reservation.checkOut', checkOut)
      const nights = getDayDiff(checkIn, checkOut)
      setValue('nights', nights)

      setValue('adults', quote.adults)
      setValue('children', quote.children)
      setValue('customerCompanyId', quote.customerCompanyId)
      setValue('credit', quote.credit)
      setValue('customerPersonId', quote.customerPersonId ?? '')

      setCartRows(
        quote.rows.map((row) => {
          return {
            ...row,
            start: new Date(row.start),
            end: new Date(row.end),
          }
        }),
      )
    }

    const rt = resourceTypesResult?.resourceTypes?.find((rt) => rt.id === props.planningInfo?.resourceTypeId)
    if (props.planningInfo && rt) {
      setValue('adults', rt.persons)
    }
  }, [resourceTypesResult])

  useEffect(() => {
    const elements = Object.keys(errors)
      .map((name) => document.getElementById(name))
      .filter((el) => !!el)
    elements.sort((a, b) => (b?.scrollHeight ?? 0) - (a?.scrollHeight ?? 0))
    elements[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [errors])

  const [noteField, setNoteField] = useState('')

  useEffect(() => {
    setValue('notes', noteField)
  }, [noteField])

  return (
    <>
      <FormLayout
        title={t('pms.booking.reservation_new.title')}
        {...formLayoutProps}
        appearance={{
          ...formLayoutProps.appearance,
          rightNodes: props.onGoToCreateGroupReservation
            ? [
                <PageHeaderItem
                  icon={faUsers}
                  text={sm ? t('pms.booking.reservation_new.go_to_group_reservation') : ''}
                  start={watch('reservation.checkIn') ?? undefined}
                  end={watch('reservation.checkOut') ?? undefined}
                  customerId={watch('customerPersonId')}
                  companyId={watch('customerCompanyId') ?? undefined}
                  note={watch('notes')}
                  onInitialValuesRetrieved={(start, end, customerId, companyId, note) => {
                    if (start) setValue('reservation.checkIn', start)
                    if (end) setValue('reservation.checkOut', end)
                    if (customerId) setValue('customerPersonId', customerId)
                    if (companyId) setValue('customerCompanyId', companyId)
                    if (note) setNoteField(note)
                  }}
                  onButtonClick={props.onGoToCreateGroupReservation}
                />,
              ]
            : undefined,
        }}
        toolbar={
          <ReservationInsertToolbar
            confirmButton={{
              disabled: loading,
              clickHandler: onFormSubmit,
              text: t('pms.booking.reservation_new.save_and_close'),
            }}
            cancelButton={{
              disabled: loading,
              clickHandler: props.onCancel,
            }}
            confirmAndOpenButton={
              props.onSubmitAndOpenReservation
                ? {
                    disabled: loading,
                    clickHandler: async () => {
                      await onFormSubmitAndOpenReservation(getValues())
                    },
                  }
                : undefined
            }
            dateRange={{
              checkIn: watch('reservation.checkIn'),
              checkOut: watch('reservation.checkOut'),
              nights: formValue.nights,
            }}
            resources={cartRows.length}
            occupancy={{
              adults: adultsOccupancy,
              children: childrenOccupancy,
            }}
            averageDailyPrice={cartRows.map((row) => row.averagePrice).reduce((acc, val) => acc + val, 0)}
            totalAccomodationPrice={cartRows.map((row) => (row.initialDailyPrices?.length ? row.totalPrice : row.averagePrice * row.nights)).reduce((acc, val) => acc + val, 0)}
            additionalServicesPrice={
              cartRows
                .map((row) =>
                  row.addedAdditionalServices?.map((as) =>
                    calculateAdditionalServicePrice({
                      adults: row.occupancy.adults,
                      children: row.occupancy.children,
                      price: as.price,
                      days: row.nights,
                      pricingType: as.pricingType,
                      automaticForAdults: as.automaticForAdults,
                      automaticForChildren: as.automaticForChildren,
                      quantity: as.quantity,
                      childPrices: as.childPrices,
                      priceZero: as.priceZero,
                    }),
                  ),
                )
                .flat()
                .filter((value) => value !== undefined)
                .reduce((prev, acc) => (prev ?? 0) + (acc ?? 0), 0) ?? 0
            }
            currency={currency}
            onClick={() => {
              cartContainerRef.current?.scrollIntoView({ behavior: 'smooth' })
            }}
            nights={formValue.nights}
          />
        }
      >
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Stack style={{ flexWrap: 'wrap' }} direction={'row'} spacing={0} justifyContent={'flex-start'} alignItems={'center'} gap={1}>
              <Controller<Reservation, 'reservation'>
                name={'reservation'}
                control={control}
                render={({ field: { onChange } }) => (
                  <HicDateRangePicker
                    fieldSmall
                    minDate={addDays(new Date(hotelDate), -1)}
                    value={reservationDateRange}
                    onChange={(newValue) => {
                      confirmCartItemReset(() => {
                        const dateRange = { checkIn: newValue[0], checkOut: newValue[1] }
                        if (dateRange.checkIn && dateRange.checkOut && dateRange.checkIn >= dateRange.checkOut) {
                          const tomorrow = new Date(dateRange.checkIn)
                          tomorrow.setDate(tomorrow.getDate() + 1)
                          dateRange.checkOut = tomorrow
                        }
                        onChange(dateRange)
                        onReservationDateRangeChange(dateRange)
                      })
                    }}
                    start={{
                      startLabel: t('pms.booking.reservation_new.check_in'),
                      startDateError: errors.reservation?.checkIn ? t('commons.required_field') : undefined,
                      maxWidth: sm ? '130px' : undefined,
                    }}
                    end={{
                      endLabel: t('pms.booking.reservation_new.check_out'),
                      endDateError: errors.reservation?.checkOut ? t('commons.required_field') : undefined,
                      maxWidth: sm ? '130px' : undefined,
                    }}
                  />
                )}
              />
              <Controller<Reservation, 'nights'>
                name={'nights'}
                control={control}
                render={({ field: { onChange } }) => (
                  <FormNumericField
                    min={1}
                    size="small"
                    fieldLabel={t('pms.booking.reservation_new.nights')}
                    disabled={props.filter?.start ? true : false}
                    errorMessage={errors.reservation?.checkOut ? t('commons.required_field') : undefined}
                    value={reservationNights}
                    sx={{
                      maxWidth: sm ? '130px' : '100%',
                      width: !sm ? '166px' : undefined,
                    }}
                    onChange={(nights) =>
                      confirmCartItemReset(() => {
                        if (nights != null) {
                          onChange(nights)
                          onNightsChange(nights)
                        }
                      })
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <FontAwesomeIcon icon={faMoon} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Stack>
          </Grid>
          <Grid item xs={12}></Grid>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Stack style={{ flexWrap: 'wrap' }} direction={'row'} spacing={0} justifyContent={'flex-start'} alignItems="flex-end" gap={1}>
                  <Controller<Reservation, 'adults'>
                    name={'adults'}
                    control={control}
                    defaultValue={2}
                    render={({ field: { value, onChange } }) => (
                      <FormNumericField
                        size="small"
                        fullWidth
                        min={1}
                        maxWidth="80px"
                        value={value}
                        onFocus={(event) => event.target.select()}
                        onChange={onChange}
                        errorMessage={errors.adults?.message}
                        fieldLabel={t('pms.booking.reservation_new.adults')}
                        // fieldIcon={
                        //   <HiCIcon>
                        //     <FontAwesomeIcon icon={faPerson} style={{ marginRight: '5' }} />
                        //   </HiCIcon>
                        // }
                      />
                    )}
                  />

                  {!showChildrenFilter && (childrenResult?.children ?? []).length > 0 && (
                    <FormItem forceLabelMargin>
                      <Button size="small" variant="text" startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setShowChildrenFilter(true)}>
                        {t('pms.booking.reservation_new.show_children_button')}
                      </Button>
                    </FormItem>
                  )}

                  {showChildrenFilter && (
                    <Controller<Reservation, 'children'>
                      name={'children'}
                      control={control}
                      defaultValue={[]}
                      render={({ field: { value, onChange } }) => (
                        <>
                          {childrenResult?.children
                            ?.slice()
                            ?.sort((first, second) => {
                              return second.maxAge - first.maxAge
                            })
                            ?.map((child, index) => {
                              // const childrenNumber = childrenResult?.children?.length ?? 0
                              // const childrenIconSize = 10 + (childrenNumber - index)
                              // const childrenIconPaddingTop = 6 - (childrenNumber - index)

                              return (
                                <FormNumericField
                                  size="small"
                                  key={'children' + index}
                                  fieldTooltip={t('pms.booking.reservation_new.child.tooltip', {
                                    child: capitalize(localizeString(child.taxonomy.description)),
                                    minAge: child.minAge,
                                    maxAge: child.maxAge,
                                  })}
                                  maxWidth="80px"
                                  min={0}
                                  value={value?.find((c) => c.childId === child.id)?.quantity ?? 0}
                                  onFocus={(event) => event.target.select()}
                                  onChange={(newQuantity) => {
                                    const tmpValue = value ? [...value] : []
                                    const childIndex = tmpValue.findIndex((c) => c.childId === child.id)
                                    if (childIndex >= 0) {
                                      tmpValue[childIndex].quantity = newQuantity ?? 0
                                    } else {
                                      tmpValue.push({ childId: child.id, quantity: newQuantity ?? 0 })
                                    }

                                    onChange([...tmpValue.filter((v) => v.quantity > 0)])
                                  }}
                                  errorMessage={errors.children ? t('commons.generic_error') : undefined}
                                  fieldLabel={localizeString(child.taxonomy.description)}
                                  // fieldIcon={
                                  //   <HiCIcon sx={{ paddingTop: `${childrenIconPaddingTop}px` }}>
                                  //     <FontAwesomeIcon icon={faChildReaching} style={{ marginRight: '5', fontSize: childrenIconSize }} />
                                  //   </HiCIcon>
                                  // }
                                />
                              )
                            })}
                        </>
                      )}
                    />
                  )}

                  {childExist && (
                    <FormItem>
                      <Button size="small" variant="outlined" onClick={resetOccupancy} startIcon={<FontAwesomeIcon icon={faArrowRotateLeft} />}>
                        {t('commons.reset')}
                      </Button>
                    </FormItem>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} mt={1}>
            <FindCompanyConventions
              disabled={props.reservationId !== undefined}
              title={t('pms.booking.reservation_new.company_conventions')}
              propertyId={props.propertyId}
              closeOnChange
              onConventionSelected={(rate, companyId) => {
                if (rate && companyId) {
                  // setRatesFilter([rate.id])
                  // setChargeCategoriesFilter([rate.chargeCategoryId])
                  setAffiliatedRateId(rate.id)
                  setValue('customerCompanyId', companyId)
                  // setDisableFilters(true)
                }
              }}
              clearConventionSelected={() => {
                if (watch('customerCompanyId')) {
                  // setRatesFilter([])
                  // setChargeCategoriesFilter([])ù
                  setAffiliatedRateId(undefined)
                  setValue('customerCompanyId', null)
                  // setDisableFilters(false)
                }
              }}
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            <HistoricalCustomerResearch tenantId={tenantId} propertyId={props.propertyId} />
          </Grid>
        </Grid>

        <Grid container spacing={1} mt={1}>
          <Grid item xs={12}>
            <HICTitle title={t('pms.booking.reservation_new.resource_title')} faIcon={faBed} />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormItem fullWidth fieldLabel={t('pms.booking.reservation_new.resource.filter_by_rate')}>
              <RatesMultipleSelect
                disabled={disableFilters}
                small
                propertyId={props.propertyId}
                value={ratesFilter}
                onChange={(ratesId: string[]) => setRatesFilter(ratesId)}
                addRateIdToOption={affiliatedRateId}
              />
            </FormItem>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormItem fullWidth fieldLabel={t('pms.booking.reservation_new.resource.filter_by_charge_category')}>
              <ChargeCategoriesMultipleSelect
                disabled={disableFilters}
                size="small"
                fullWidth
                value={chargeCategoriesFilter}
                onChange={(value) => setChargeCategoriesFilter(value)}
                propertyId={props.propertyId}
                error={undefined}
              />
            </FormItem>
          </Grid>
          <Grid item xs={12}>
            {accordionDetails}
          </Grid>

          <Grid item xs={12} ref={additionalServicesContainerRef}>
            <HICTitle title={t('pms.booking.reservation_new.additional_services_title')} faIcon={faPuzzlePiece} />
            <Stack direction="column">
              <AdditionalServicesAccordion
                propertyId={props.propertyId}
                additionalServices={enabledAdditionalServices}
                cartRows={cartRows}
                updateCartRows={(updatedRows) => {
                  const updatedRowsId = updatedRows.map((row) => row.id)
                  const tmpRows = cartRows.map((cartRow) => {
                    const tmp = { ...cartRow }
                    if (updatedRowsId.includes(cartRow.id)) {
                      const updatedAdditionalServices = updatedRows.find((row) => row.id === cartRow.id)?.addedAdditionalServices?.filter((as) => as.added) ?? []
                      if (updatedAdditionalServices) {
                        tmp.addedAdditionalServices = updatedAdditionalServices
                      }
                    }
                    return tmp
                  })
                  setCartRows(tmpRows)
                  successSnackBar(t('pms.booking.reservation_new.additional_service_added_message'))
                }}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} ref={cartContainerRef} mt={4}>
            {cartComponent}
          </Grid>

          <Grid item xs={12} mt={4}>
            <HICTitle title={t('pms.booking.reservation_new.booking_notes_title')} faIcon={faNoteSticky} />

            <Controller<Reservation, 'notes'>
              name={'notes'}
              control={control}
              render={() => (
                <FormItem errorMessage={errors.notes?.message} fullWidth>
                  <TextField
                    size="small"
                    disabled={props.reservationId !== undefined}
                    value={noteField}
                    onChange={(event) => setNoteField(event.target.value)}
                    multiline
                    placeholder={t('pms.booking.reservation_new.booking_notes_placeholder')}
                  />
                </FormItem>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <HICTitle title={t('pms.booking.reservation_new.reservant')} faIcon={faUser} />

            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller<Reservation, 'customerPersonId'> //
                  name={'customerPersonId'}
                  control={control}
                  render={({ field }) => (
                    <div id={'customerPersonId'}>
                      <FormItem
                        errorMessage={errors?.customerPersonId?.message}
                        fullWidth
                        fieldLabel={t('pms.booking.reservation_new.person')}
                        fieldIcon={
                          <HiCIcon sx={{ pr: 1 }}>
                            <FontAwesomeIcon icon={faUser} />
                          </HiCIcon>
                        }
                      >
                        <PersonLookup
                          size="small"
                          disabled={loading || props.reservationId !== undefined}
                          value={customerPersonId ? { id: customerPersonId } : null}
                          onChange={(value) => {
                            field.onChange(value?.id || null)
                            setCurrentGuest(value)
                            const mailAddress = value?.mailContacts?.find((m) => m.isDefault)?.value || undefined
                            if (mailAddress) setValue('customerEmailAddress', mailAddress)

                            if (value?.relatedCompanyId) {
                              setValue('customerCompanyId', value?.relatedCompanyId)
                            }
                          }}
                          minSearchParameterLenght={2}
                          onBlur={(value) => {
                            field.onBlur()
                            onBlurHandlerPersonLookup(value)
                          }}
                          updateAction
                          createAction
                          showEmail={true}
                          showPhoneNumber={true}
                          showTags={true}
                          showRelatedCompany={true}
                        />
                      </FormItem>
                      {currentGuest !== null ? (
                        <CustomerDetailsEdit
                          value={currentGuest}
                          type={CustomerType.PERSON}
                          onChange={(value) => {
                            setCurrentGuest(value)
                            if (!getValues().disableAutomation) {
                              const mailAddress = value?.mailContacts?.find((m) => m.isDefault)?.value || undefined
                              if (mailAddress) setValue('customerEmailAddress', mailAddress)
                            }
                            if (value === null) setValue('customerPersonId', '')
                          }}
                          onErrors={(e) => {
                            if (e?.mailContact?.message) setValue('customerEmailAddress', null)
                          }}
                          requireEmailAddress={!getValues().disableAutomation}
                        />
                      ) : undefined}
                    </div>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller<Reservation, 'customerCompanyId'> //
                  name={'customerCompanyId'}
                  control={control}
                  render={({ field }) => (
                    <>
                      <FormItem
                        errorMessage={errors?.customerCompanyId?.message}
                        fullWidth
                        fieldLabel={t('pms.booking.reservation_new.company')}
                        fieldIcon={
                          <HiCIcon sx={{ pr: 1 }}>
                            <FontAwesomeIcon icon={faBuilding} />
                          </HiCIcon>
                        }
                      >
                        <CompanyLookup //
                          size="small"
                          error={errors?.customerCompanyId ? true : false}
                          value={customerCompanyId ? { id: customerCompanyId } : null}
                          onChange={(value) => {
                            setValue('credit', value?.company?.defaultCredit ?? CreditType.NC)
                            field.onChange(value?.id || null)
                            setCurrentCompany(value)
                          }}
                          disabled={loading || props.reservationId !== undefined}
                          // clearOnBlur={false}
                          onBlur={onBlurHandlerCompanyLookup}
                          createAction
                          updateAction
                          showTags={true}
                        />
                      </FormItem>
                      {currentCompany !== null ? (
                        <CustomerDetailsEdit
                          value={currentCompany}
                          type={CustomerType.COMPANY}
                          onChange={(value) => {
                            setCurrentCompany(value)
                            if (value === null) setValue('customerCompanyId', '')
                          }}
                        />
                      ) : undefined}
                    </>
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {props.reservationId === undefined && (
            <>
              <Grid item xs={12}>
                <HICTitle title={t('pms.booking.reservation_new.options')} faIcon={faBallotCheck} />

                <Grid item container xs={12} style={{ paddingLeft: '16px' }} spacing={0}>
                  <Controller<Reservation, 'disableAutomation'> //
                    name={'disableAutomation'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel //
                        control={<Checkbox value={value} onChange={onChange} />}
                        label={t('pms.booking.reservation_new.disable_automation')}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} style={{ paddingLeft: '16px' }}>
                  <Stack direction="column">
                    <Controller<Reservation, 'isOptioned'> //
                      name={'isOptioned'}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel //
                          control={
                            <Checkbox
                              defaultChecked={false}
                              value={value}
                              onChange={(event, checked) => {
                                if (!checked) setValue('optionedExpirationDate', null)
                                onChange(event, checked)
                              }}
                            />
                          }
                          label={`${t('pms.booking.reservation_new.optioned')}`}
                        />
                      )}
                    />
                    <Controller<Reservation, 'optionedExpirationDate'> //
                      name={'optionedExpirationDate'}
                      control={control}
                      render={({ field }) => (
                        <FormItem //
                          fieldLabel={getValues().isOptioned ? t('pms.booking.reservation_new.optioned_expiration_date') : ''}
                          errorMessage={errors.optionedExpirationDate?.message}
                          style={{ paddingLeft: '30px', display: !getValues().isOptioned ? 'none' : 'block' }}
                        >
                          <DatePicker //
                            disabled={loading || !getValues().isOptioned}
                            value={field.value ?? null}
                            minDate={new Date()}
                            maxDate={getValues().reservation.checkIn}
                            open={openOptionedDatePicker}
                            onOpen={() => setOpenOptionedDatePicker(true)}
                            onClose={() => setOpenOptionedDatePicker(false)}
                            onChange={(date) => {
                              if (date && isValid(date)) {
                                field.onChange(date)
                                setOpenOptionedDatePicker(false)
                              } else {
                                field.onChange(null)
                              }
                            }}
                            slotProps={{ textField: { size: 'small' } }}
                          />
                        </FormItem>
                      )}
                    />
                  </Stack>
                </Grid>
              </Grid>
              <Grid item container xs={12} style={{ paddingLeft: '10px' }} spacing={0}>
                {formValue.customerCompanyId && (
                  <Controller<Reservation, 'credit'> //
                    name={'credit'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormItem style={{ minWidth: '200px', marginLeft: '15px' }} fieldLabel={t('pms.booking.reservation_new.options.credit_label')}>
                        <CreditSingleSelect value={value} onChange={onChange} />
                      </FormItem>
                    )}
                  />
                )}
              </Grid>
            </>
          )}
        </Grid>

        <Grid container spacing={1} height="200px"></Grid>

        {loading && <HICLoading />}
      </FormLayout>

      <Dialog open={openOverbookingModal.length > 0} onClose={handleCloseOverbookingModal}>
        <DialogContent>
          <DialogContentText>{t('pms.quote.read_only.summary.confirm_quotation_overbooking_dialog.message')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseOverbookingModal}>
            {t('commons.no')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const confirmedStateId = reservationStatesResult?.reservationStates?.find((state) => state.confirmed === true)?.id
              const originId = originsResult?.origins ? originsResult.origins[0].id : undefined
              const formValue = watch()
              const reservationFormData = formSchemaAsReservationFormData(formValue)
              if (originId && confirmedStateId && reservationFormData) {
                const formData = reservationNewCartAsFormData(
                  tenantId,
                  props.propertyId,
                  originId,
                  reservationFormData,
                  confirmedStateId,
                  cartRows.filter((row) => openOverbookingModal.includes(row.id)),
                )
                submitWithOverbooking(formData)
              }
              handleCloseOverbookingModal()
            }}
            autoFocus
          >
            {t('commons.yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
